import { useEffect, useMemo, useState } from "react";
import { CanAccess, useNotify } from "ra-core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import { Flag, Loader2, RefreshCw, ShieldAlert } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import type { PublicEchoFeedbackRecord } from "@/server/echoFeedback";
import { EmptyHint } from "../echoflicks/EchoflicksSessionList";
import { FeedbackDetailsDialog } from "./FeedbackDetailsDialog";
import { FeedbackFilters, type FeedbackDateFilter } from "./FeedbackFilters";
import { FeedbackTable } from "./FeedbackTable";
import { feedbackApiRequest } from "./echoFeedbackClient";

type FeedbackListResponse = {
  data: PublicEchoFeedbackRecord[];
  configured: boolean;
  storageMode?: string;
  total?: number;
  corruptCount?: number;
  legacyCount?: number;
  truncated?: boolean;
};

/** Internal operator-QC feedback with category and captured metadata drill-down. */
export const EchoFeedbackPage = () => (
  <CanAccess resource="echo_feedback" action="list">
    <EchoFeedbackPageContent />
  </CanAccess>
);

export const EchoFeedbackPageContent = () => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedEchoId = searchParams.get("echoId");
  const feedbackQuery = useQuery({
    queryKey: ["echo-feedback", "operator-qc", linkedEchoId ?? "all"],
    queryFn: () => {
      const query = new URLSearchParams({ limit: "500" });
      if (linkedEchoId) query.set("echoId", linkedEchoId);
      return feedbackApiRequest<FeedbackListResponse>(
        `/api/echo-feedback?${query}`,
      );
    },
  });
  const result = feedbackQuery.data ?? null;
  const [selected, setSelected] = useState<PublicEchoFeedbackRecord | null>(
    null,
  );
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [metadataFilter, setMetadataFilter] = useState("all");
  const [notificationFilter, setNotificationFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState<FeedbackDateFilter>("all");
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const linkedFeedbackId = searchParams.get("feedbackId");
  const responseLimited = Boolean(
    result && (result.total ?? result.data.length) > result.data.length,
  );

  useEffect(() => {
    if (!linkedFeedbackId || !result?.data.length) return;
    const linkedRecord = result.data.find(
      (record) => record.id === linkedFeedbackId,
    );
    if (linkedRecord) setSelected(linkedRecord);
  }, [linkedFeedbackId, result?.data]);

  const openDetails = (record: PublicEchoFeedbackRecord) => {
    setSelected(record);
    const next = new URLSearchParams(searchParams);
    next.set("feedbackId", record.id);
    setSearchParams(next, { replace: true });
  };

  const closeDetails = () => {
    setSelected(null);
    if (!searchParams.has("feedbackId")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("feedbackId");
    setSearchParams(next, { replace: true });
  };

  const records = useMemo(
    () =>
      (result?.data ?? []).filter((record) => {
        if (
          categoryFilter !== "all" &&
          !record.categories.some((category) => category.id === categoryFilter)
        ) {
          return false;
        }
        if (
          metadataFilter !== "all" &&
          record.metadata.status !== metadataFilter
        ) {
          return false;
        }
        if (
          notificationFilter !== "all" &&
          record.notification.status !== notificationFilter
        ) {
          return false;
        }
        if (dateFilter !== "all") {
          const days = dateFilter === "7d" ? 7 : 30;
          if (
            Date.parse(record.submittedAt) <
            Date.now() - days * 24 * 60 * 60 * 1000
          ) {
            return false;
          }
        }
        return true;
      }),
    [
      categoryFilter,
      dateFilter,
      metadataFilter,
      notificationFilter,
      result?.data,
    ],
  );

  const retryNotification = async (record: PublicEchoFeedbackRecord) => {
    setRetryingId(record.id);
    try {
      const url = new URL("/api/echo-feedback", window.location.origin);
      url.searchParams.set("action", "retry");
      url.searchParams.set("id", record.id);
      const response = await feedbackApiRequest<{
        data: PublicEchoFeedbackRecord;
      }>(`${url.pathname}${url.search}`, { method: "POST" });
      queryClient.setQueryData<FeedbackListResponse>(
        ["echo-feedback", "operator-qc", linkedEchoId ?? "all"],
        (current) =>
          current
            ? {
                ...current,
                data: current.data.map((item) =>
                  item.id === record.id ? response.data : item,
                ),
              }
            : current,
      );
      setSelected((current) =>
        current?.id === record.id ? response.data : current,
      );
      notify(
        response.data.notification.status === "sent"
          ? "Slack alert sent"
          : "Feedback is saved, but the Slack alert is still pending",
        {
          type:
            response.data.notification.status === "sent"
              ? "success"
              : "warning",
        },
      );
    } catch (caught) {
      notify(
        caught instanceof Error
          ? caught.message
          : "Could not retry Slack alert",
        { type: "error" },
      );
    } finally {
      setRetryingId(null);
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-4 py-4">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Flag className="size-5 text-destructive" />
                Echo feedback
              </CardTitle>
              <CardDescription>
                Internal timestamped QC reports. Rich diagnostic snapshots stay
                private here; Slack receives only category labels and a generic
                link to the authenticated Feedback tab.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void feedbackQuery.refetch()}
              disabled={feedbackQuery.isFetching}
            >
              {feedbackQuery.isFetching ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <RefreshCw className="size-4" />
              )}
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {result &&
          (result.corruptCount || result.truncated || responseLimited) ? (
            <Alert variant="destructive">
              <ShieldAlert className="size-4" />
              <AlertTitle>Some stored feedback needs attention</AlertTitle>
              <AlertDescription>
                {result.corruptCount
                  ? `${result.corruptCount} corrupt report${result.corruptCount === 1 ? " was" : "s were"} skipped. `
                  : ""}
                {result.truncated
                  ? "The feedback store scan reached its safety ceiling, so additional reports may be missing. "
                  : ""}
                {responseLimited
                  ? `Only the newest ${result.data.length} of ${result.total} reports were loaded. Use the migration/export utility to inspect the complete set.`
                  : ""}
              </AlertDescription>
            </Alert>
          ) : null}

          {result &&
          (!result.configured || result.storageMode !== "private-oidc") ? (
            <Alert variant="destructive">
              <ShieldAlert className="size-4" />
              <AlertTitle>Private feedback storage is not writable</AlertTitle>
              <AlertDescription>
                {result.configured
                  ? "Legacy reports are readable, but this deployment is still connected to the public read-only compatibility store. Connect its environment-local private Blob store before accepting new feedback."
                  : "This deployment has no feedback store connection. Connect its environment-local private Blob store before accepting new feedback."}
              </AlertDescription>
            </Alert>
          ) : null}

          <FeedbackFilters
            category={categoryFilter}
            metadata={metadataFilter}
            notification={notificationFilter}
            date={dateFilter}
            onCategory={setCategoryFilter}
            onMetadata={setMetadataFilter}
            onNotification={setNotificationFilter}
            onDate={setDateFilter}
          />

          {feedbackQuery.error ? (
            <Alert variant="destructive">
              <AlertTitle>Could not load feedback</AlertTitle>
              <AlertDescription>
                {feedbackQuery.error instanceof Error
                  ? feedbackQuery.error.message
                  : "Could not load feedback"}
              </AlertDescription>
            </Alert>
          ) : feedbackQuery.isLoading && result === null ? (
            <EmptyHint>Loading feedback reports...</EmptyHint>
          ) : !records.length ? (
            <EmptyHint>
              {result?.data.length
                ? "No feedback matches these filters."
                : "No feedback yet. Open an Echo and use Feedback here at the relevant moment."}
            </EmptyHint>
          ) : (
            <FeedbackTable
              records={records}
              onSelect={openDetails}
              onRetry={retryNotification}
              retryingId={retryingId}
            />
          )}

          {result ? (
            <p className="text-xs text-muted-foreground">
              Showing {records.length} of {result.total ?? result.data.length}
              {result.legacyCount
                ? ` · ${result.legacyCount} migrated legacy report${result.legacyCount === 1 ? "" : "s"}`
                : ""}
              {result.storageMode ? ` · ${result.storageMode}` : ""}
            </p>
          ) : null}
        </CardContent>
      </Card>

      <FeedbackDetailsDialog
        record={selected}
        retrying={selected?.id === retryingId}
        onRetry={retryNotification}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) closeDetails();
        }}
      />
    </div>
  );
};
